import { useMemo } from 'react';
import { useAuth } from '../state/AuthContext';
import { PLAN_NAME, PLAN_TYPE, featureForPath } from '../components/common/Helper';

/**
 * Everything the UI needs to know about the current plan, in one place.
 *
 * Derived from the two keys the subscription API returns - planName
 * ('Basic'/'Standard') and planType (TRIAL/PAID) - which AuthContext resolves
 * once and caches. To change what a plan unlocks, edit PLAN_FEATURES and
 * PLAN_RECORD_LIMITS in Helper.js; nothing here needs touching.
 *
 * @returns {{
 *   isLoading: boolean,      subscription state not resolved yet
 *   isLocked: boolean,       no live subscription - show the subscribe wall
 *   planName: string|null,
 *   planType: string|null,
 *   isTrial: boolean,
 *   isBasic: boolean,
 *   isStandard: boolean,
 *   maxRecords: number|null, rows a table may show; null means uncapped
 *   can: (feature: string) => boolean,
 *   canAccessPath: (path: string) => boolean,
 * }}
 */
export default function usePlanAccess() {
  const { isSubscriptionActive, planAccess } = useAuth();

  return useMemo(() => {
    const isLoading = isSubscriptionActive === null;
    const isLocked = isSubscriptionActive === false;
    const features = planAccess?.features ?? {};

    /** Is this feature part of the current plan? */
    const can = (feature) => {
      // While loading, assume allowed so pages don't flicker into a locked
      // state and bounce the user away before the plan is known.
      if (isLoading) return true;
      if (isLocked) return false;
      return features[feature] === true;
    };

    /** Is this route reachable? Unmapped routes are always allowed. */
    const canAccessPath = (path) => {
      const feature = featureForPath(path);
      if (!feature) return true;
      return can(feature);
    };

    return {
      isLoading,
      isLocked,
      planName: planAccess?.planName ?? null,
      planType: planAccess?.planType ?? null,
      isTrial: planAccess?.isTrial === true,
      isBasic: planAccess?.planName === PLAN_NAME.BASIC,
      isStandard: planAccess?.planName === PLAN_NAME.STANDARD,
      // Only cap once we actually know the plan - capping while loading would
      // truncate the first render of every table.
      maxRecords: isLoading ? null : (planAccess?.maxRecords ?? null),
      can,
      canAccessPath,
    };
  }, [isSubscriptionActive, planAccess]);
}

export { PLAN_NAME, PLAN_TYPE };
